import { useEffect, useState } from "react";
import { Col, Form, Modal, Row } from "react-bootstrap";
import { Input } from "../../components/Input/input";
import { useForm } from "react-hook-form";
import { Portal } from "@mui/base";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Button, { ButtonColors, ButtonTypes } from "../../components/Button";
import { UserService } from "../../services/UsersService";
import { writePasswordResetSentContext } from "../../utils/recoveryFlowStorage";
import { ReauthService } from "../../services/ReauthService";

type UpdatePasswordProps = {
  poolId: string;
  tenant: string;
  email: string;
};

export default function UpdatePassword({
  poolId,
  tenant,
  email,
}: UpdatePasswordProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showChooseModal, setShowChooseModal] = useState(false);
  const [primaryDisplay, setPrimaryDisplay] = useState("");
  const [recoveryMasked, setRecoveryMasked] = useState("");
  const [responseType, setResponseType] = useState<
    "email" | "recoveryEmail"
  >("email");

  async function openModal() {
    if (!(await ReauthService.isAuthFreshForSensitive())) {
      return ReauthService.triggerReauth({
        action: "update-password",
        tab: "tab2",
      });
    }

    handleAlterClick();
  }

  useEffect(() => {
    if (ReauthService.consumePendingState("update-password")) {
      toast.info("Identidade confirmada. Clique em Alterar para continuar.");
    }
  }, []);

  const navigateToSent = (
    trimmedEmail: string,
    channel: "email" | "recoveryEmail",
  ) => {
    writePasswordResetSentContext({
      email: trimmedEmail,
      response_type: channel,
    });
    navigate({
      pathname: `/${poolId}/${tenant}/password-reset-link-sent`,
    });
  };

  async function confirmSendLink(nextType: "email" | "recoveryEmail") {
    const trimmed = email.trim();
    setLoading(true);
    try {
      await UserService.sendPasswordRecoveryLink(poolId, trimmed, nextType);
      setShowChooseModal(false);
      navigateToSent(trimmed, nextType);
    } catch {
      toast.error(
        "Não foi possível enviar o link de redefinição de senha. Tente novamente.",
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleAlterClick() {
    const trimmed = email.trim();
    if (!trimmed || !poolId || !tenant) {
      toast.error(
        "Não foi possível identificar o pool ou e‑mail para continuar.",
      );
      return;
    }

    setLoading(true);
    try {
      const { data } = await UserService.getRecoveryEmail(poolId, trimmed);
      const recovery =
        typeof data?.recovery_email === "string"
          ? data.recovery_email
          : null;
      const main =
        typeof data?.email === "string" ? data.email : trimmed;

      if (recovery) {
        setPrimaryDisplay(main);
        setRecoveryMasked(recovery);
        setResponseType("email");
        setShowChooseModal(true);
        return;
      }

      await UserService.sendPasswordRecoveryLink(poolId, trimmed, "email");
      navigateToSent(trimmed, "email");
    } catch {
      toast.error(
        "Não foi possível verificar o e‑mail ou o envio falhou. Verifique seus dados.",
      );
    } finally {
      setLoading(false);
    }
  }

  const handleModalConfirm = () => {
    confirmSendLink(responseType);
  };

  return (
    <>
      <Button
        text={"Alterar"}
        color={ButtonColors.primary}
        isLoading={loading}
        onClick={openModal}
      />

      <Portal>
        <Modal
          show={showChooseModal}
          onHide={() => !loading && setShowChooseModal(false)}
          centered
        >
          <Modal.Header closeButton={!loading}>
            <Modal.Title>Enviar link de alteração de senha</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted small mb-3">
              Selecione o e‑mail onde deseja receber o link para redefinir sua senha:
            </p>
            <div className="d-flex flex-column gap-3">
              <Form.Check
                id="recovery-to-primary"
                type="radio"
                name="recoveryTarget"
                label={primaryDisplay || email}
                checked={responseType === "email"}
                onChange={() => setResponseType("email")}
                disabled={loading}
              />
              <Form.Check
                id="recovery-to-alt"
                type="radio"
                name="recoveryTarget"
                label={recoveryMasked || "E‑mail de recuperação"}
                checked={responseType === "recoveryEmail"}
                onChange={() => setResponseType("recoveryEmail")}
                disabled={loading}
              />
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              text={"Cancelar"}
              color={ButtonColors.warn}
              disabled={loading}
              onClick={() => setShowChooseModal(false)}
              type={ButtonTypes.button}
            />
            <Button
              text={"Enviar link"}
              color={ButtonColors.primary}
              isLoading={loading}
              onClick={handleModalConfirm}
              type={ButtonTypes.button}
            />
          </Modal.Footer>
        </Modal>
      </Portal>
    </>
  );
}
